import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, Users, ArrowRight } from 'lucide-react';
import { useVideoPlayer } from '@/hooks/communities/montana';
import { useMontanaData } from '@/hooks/communities/montana';

interface VideoShowcaseProps {
  className?: string;
}

export const VideoShowcase: React.FC<VideoShowcaseProps> = ({ className = '' }) => {
  const { currentVideo, playVideo, isPlaying } = useVideoPlayer();
  const { videoDemos } = useMontanaData();
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const handleVideoClick = (videoId: string) => {
    setSelectedVideoId(videoId);
    playVideo(videoId);
  };

  return (
    <div className={`py-16 bg-white ${className}`}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-100 text-blue-800">
            <Play className="w-4 h-4 mr-2" />
            Interactive Demonstrations
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            See Your Platform in Action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Watch real workflows and see exactly how your Montana community will benefit from modern
            digital management. Each video shows a complete user journey.
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {videoDemos.map((video, index) => (
            <Card
              key={video.id}
              className={`group cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                selectedVideoId === video.id ? 'ring-2 ring-blue-500 shadow-lg' : ''
              }`}
              onClick={() => handleVideoClick(video.id)}
            >
              <CardHeader className="p-0">
                {/* Video Thumbnail */}
                <div className="relative overflow-hidden rounded-t-lg">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  />

                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white rounded-full p-4 shadow-lg">
                      <Play className="w-8 h-8 text-blue-600 fill-current" />
                    </div>
                  </div>

                  {/* Duration Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-black bg-opacity-70 text-white border-0">
                      <Clock className="w-3 h-3 mr-1" />
                      {video.duration}
                    </Badge>
                  </div>

                  {/* Video Number */}
                  <div className="absolute top-3 left-3">
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <CardTitle className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {video.title}
                    </CardTitle>
                    <p className="text-gray-600 text-sm line-clamp-2">{video.description}</p>
                  </div>

                  {/* User Flow */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-gray-700 mb-1">User Flow:</div>
                    <div className="text-sm text-gray-600 font-mono">{video.userFlow}</div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors"
                  >
                    {currentVideo === video.id && isPlaying ? 'Playing...' : 'Watch Demo'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="mt-12 text-center">
          <Card className="bg-linear-to-r from-blue-50 to-indigo-50 border-blue-200 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Want a Personalized Demo?
                  </h3>
                  <p className="text-gray-600">
                    Schedule a live demonstration tailored to your Montana community's specific
                    needs. Our experts will show you exactly how the platform works with your data.
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    <Users className="w-5 h-5 mr-2" />
                    Schedule Live Demo
                  </Button>
                  <div className="text-sm text-gray-500">30 minutes • No obligation</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Video Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">12 min</div>
            <div className="text-sm text-gray-600">Total Watch Time</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">5</div>
            <div className="text-sm text-gray-600">Complete Workflows</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">100%</div>
            <div className="text-sm text-gray-600">Real Platform</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">0</div>
            <div className="text-sm text-gray-600">Sales Pressure</div>
          </div>
        </div>
      </div>
    </div>
  );
};
